/**
 * 租户门户认证相关API
 */

import { request } from '@/utils/http'
import type {
  RegisterCodeRequest,
  RegisterRequest,
  LoginRequest,
  LoginCodeRequest,
  LoginVerifyRequest,
  PasswordResetCodeRequest,
  PasswordResetRequest,
  LoginResponse
} from '@/types/auth'

/**
 * Code换取Token的返回类型
 */
export interface CodeToTokenResponse {
  token: string
  userType: number
}
// ==================== Code/Token 转换 ====================

/**
 * Code换取Token
 * @param code 6位验证码
 * @returns Promise<CodeToTokenResponse> 返回Token和用户类型
 */
export const convertCodeToToken = (code: string): Promise<CodeToTokenResponse> => {
  return request.post<CodeToTokenResponse>('/enterprise/tenant/user/login/token', { code })
}

/**
 * Token转换为Code
 * @returns Promise<string> 返回6位Code
 */
export const convertTokenToCode = (): Promise<string> => {
  return request.post<string>('/enterprise/tenant/user/convertToken', {})
}

// ==================== 用户详情 ====================

/**
 * 获取租户用户详情
 * @returns Promise<UserDetailResponse> 用户详情
 */
export const getTenantUserDetail = (): Promise<any> => {
  return request.post<any>('/enterprise/tenant/user/detail')
}

/**
 * 获取客户端用户详情
 * @returns Promise<UserDetailResponse> 用户详情
 */
export const getClientUserDetail = (): Promise<any> => {
  return request.post<any>('/enterprise/client/user/detail')
}

// ==================== 租户端认证API ====================

/**
 * 1. 注册发送验证码
 * @param params 请求参数 { phone }
 */
export const sendRegisterCode = (params: RegisterCodeRequest) => {
  return request.post<string>('/enterprise/tenant/user/register/code', params)
}

/**
 * 2. 用户注册
 * @param params 注册参数
 */
export const userRegister = (params: RegisterRequest) => {
  return request.post<string>('/enterprise/tenant/user/register', params)
}

/**
 * 3. 用户密码登录
 * @param params 登录参数
 */
export const userLogin = (params: LoginRequest) => {
  return request.post<LoginResponse>('/enterprise/tenant/user/login', params)
}

/**
 * 4. 登录发送验证码
 * @param params 请求参数 { account }
 */
export const sendLoginCode = (params: LoginCodeRequest) => {
  return request.post<string>('/enterprise/tenant/user/login/code', params)
}

/**
 * 5. 验证码登录
 * @param params 验证码登录参数
 */
export const userLoginVerify = (params: LoginVerifyRequest) => {
  return request.post<LoginResponse>('/enterprise/tenant/user/login/verify', params)
}

/**
 * 6. 密码重置发送验证码
 * @param params 请求参数 { account }
 */
export const sendPasswordResetCode = (params: PasswordResetCodeRequest) => {
  return request.post<string>('/enterprise/tenant/user/password/reset/code', params)
}

/**
 * 7. 重置密码
 * @param params 重置密码参数
 */
export const resetPassword = (params: PasswordResetRequest) => {
  return request.post<boolean>('/enterprise/tenant/user/password/reset', params)
}

// ==================== 客户端认证API ====================

/**
 * 1. 客户端注册发送验证码
 * @param params 请求参数 { phone }
 */
export const clientSendRegisterCode = (params: RegisterCodeRequest) => {
  return request.post<string>('/enterprise/client/user/register/code', params)
}

/**
 * 2. 客户端用户注册
 * @param params 注册参数
 */
export const clientUserRegister = (params: RegisterRequest) => {
  return request.post<LoginResponse>('/enterprise/client/user/register', params)
}

/**
 * 3. 客户端用户密码登录
 * @param params 登录参数
 */
export const clientUserLogin = (params: LoginRequest) => {
  return request.post<LoginResponse>('/enterprise/client/user/login', params)
}

/**
 * 4. 客户端登录发送验证码
 * @param params 请求参数 { account }
 */
export const clientSendLoginCode = (params: LoginCodeRequest) => {
  return request.post<string>('/enterprise/client/user/login/code', params)
}

/**
 * 5. 客户端验证码登录
 * @param params 验证码登录参数
 */
export const clientUserLoginVerify = (params: LoginVerifyRequest) => {
  return request.post<LoginResponse>('/enterprise/client/user/login/verify', params)
}

/**
 * 6. 客户端密码重置发送验证码
 * @param params 请求参数 { account }
 */
export const clientSendPasswordResetCode = (params: PasswordResetCodeRequest) => {
  return request.post<string>('/enterprise/client/user/password/reset/code', params)
}

/**
 * 7. 客户端重置密码
 * @param params 重置密码参数
 */
export const clientResetPassword = (params: PasswordResetRequest) => {
  return request.post<boolean>('/enterprise/client/user/password/reset', params)
}


/**
 * SSO 登录
 * @param params SSO登录参数
 * @returns Promise<LoginSsoResponse>
 */
export const loginSso = (params: import('@/types/auth').LoginSsoRequest) => {
  return request.post<import('@/types/auth').LoginSsoResponse>('/enterprise/tenant/user/login/sso', params)
}


/**
 * SSO 登录验证账号
 * @param params SSO登录参数
 * @returns Promise<LoginSsoResponse>
 */
export const loginSsoVerify = (params: import('@/types/auth').LoginSsoVerifyRequest) => {
  return request.post<Boolean>('/enterprise/tenant/user/login/sso/verify', params)
}

// ==================== 美格数字人 ====================

/**
 * 获取美格数字人 Token
 * 用于初始化美格数字人聊天界面
 * @returns Promise<string> 返回 Token 字符串
 */
export const getMeiGeToken = (): Promise<string> => {
  return request.post<string>('/enterprise/enterprise-config/getMeiGeToken')
}
